import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getFirestoreService } from "../firebase/services";

const TRIAL_POPUP_SEEN_SESSION_KEY = "hasSeenTrialPopup";
const TRIAL_POPUP_DELAY_MS = 1000;

const TrialPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const navigate = useNavigate();
  const closeButtonRef = useRef(null);

  const handleClose = (event) => {
    event?.stopPropagation?.();
    setIsOpen(false);
    sessionStorage.setItem(TRIAL_POPUP_SEEN_SESSION_KEY, "true");
  };

  useEffect(() => {
    let openTimer = null;

    const checkConfigAndShow = async () => {
      const hasSeenPopup = sessionStorage.getItem(TRIAL_POPUP_SEEN_SESSION_KEY);
      if (hasSeenPopup) {
        setIsOpen(false);
        return;
      }

      try {
        const db = await getFirestoreService();
        const { doc, getDoc } = await import("firebase/firestore");
        const docRef = doc(db, "settings", "trialConfig");
        const docSnap = await getDoc(docRef);
        let shouldShow = true;

        if (docSnap.exists()) {
          const data = { isActive: true, ...docSnap.data() };
          const nextIsActive = data.isActive !== false;
          setIsActive(nextIsActive);
          shouldShow = nextIsActive;
        } else {
          // Default fallback
          setIsActive(true);
          shouldShow = true;
        }

        if (shouldShow) {
          openTimer = setTimeout(() => {
            setIsOpen(true);
          }, TRIAL_POPUP_DELAY_MS);
        }
      } catch (err) {
        console.error("Error fetching popup config:", err);
      }
    };

    checkConfigAndShow();
    return () => {
      if (openTimer) clearTimeout(openTimer);
    };
  }, []);

  // Handle Escape key and focus trapping
  useEffect(() => {
    if (!isOpen) return;

    // Move focus to close button when opened
    const timer = setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 100);

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        handleClose(e);
      }
      
      // Simple focus trap: prevent tabbing out of the popup
      if (e.key === "Tab") {
        e.preventDefault();
        closeButtonRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      clearTimeout(timer);
    };
  }, [isOpen]);

  const handleImageClick = () => {
    setIsOpen(false);
    sessionStorage.setItem(TRIAL_POPUP_SEEN_SESSION_KEY, "true");
    navigate("/trial-interest");
  };

  const isVisible = isActive && isOpen;
  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm opacity-100 bg-black/70 pointer-events-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="trial-popup-title"
      aria-hidden="false"
    >
      <div
        className="relative max-w-lg w-full bg-white rounded-2xl overflow-hidden shadow-2xl focus-visible:outline-none scale-100"
        onClick={handleImageClick}
        tabIndex={-1}
      >
        <h2 id="trial-popup-title" className="sr-only">Academy Trial Session Invitation</h2>
        
        {/* Close Button */}
        <button
          ref={closeButtonRef}
          onClick={handleClose}
          className="absolute top-4 right-4 z-50 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full cursor-pointer ring-2 ring-white/80 focus:ring-2 focus:ring-[#F4B400] transition-shadow outline-none"
          aria-label="Close popup"
        >
          <X size={24} />
        </button>

        {/* Flyer Image */}
        <button 
          className="w-full h-full p-0 border-none bg-transparent cursor-pointer group relative block text-left"
          onClick={handleImageClick}
          aria-label="View trial session details and register"
        >
          <img
            alt="Reflection Futbol Academy Trial Sessions Flyer"
            width={665}
            height={998}
            fetchPriority="high"
            loading="eager"
            srcSet="/assets/trial-flyer-400w.webp 400w, /assets/trial-flyer.webp 665w"
            sizes="(max-width: 640px) 400px, 665px"
            src="/assets/trial-flyer.webp"
            className="w-full h-auto object-contain max-h-[80vh]"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <span className="bg-[#F4B400] text-black font-black py-3 px-8 rounded-full opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 shadow-xl border-2 border-white text-xs tracking-widest uppercase">
              REGISTER NOW
            </span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default TrialPopup;
